export const BLOG_POSTS = [
  {
    id: 1,
    title: "Simple App: A Production-Ready Social Platform Built with Go and React",
    excerpt:
      "A deep dive into the architecture and features of Simple App, a comprehensive social platform designed for scalability and performance.",
    content: `
      Simple App is a production-ready social media application. It features a modern tech stack and advanced social features.

      Key Features:
      • News Feed with infinite scroll and real-time interactions
      • Dynamic reactions engine
      • Rich media stories with 24h expiration and professional-grade photo editor
      • Smart video player
      • Real-time notifications and messaging
      • And many more...

      This project demonstrates my ability to build complex, full-stack applications with a focus on performance, security, and maintainability.
    `,
    date: "March 5, 2026",
    readTime: "10 min read",
    category: "Full Stack",
    image: "/images/projects/simpleapp.png",
  },
  {
    id: 2,
    title: "Building Scalable React Applications with Modern Architecture",
    excerpt:
      "Learn how to structure your React projects for maximum scalability and maintainability using the latest patterns and best practices.",
    content: `
      Building scalable React applications requires careful consideration of architecture from the start. In this article, we'll explore modern patterns that help teams build maintainable, performant applications.

      Key principles include:
      • Component composition over inheritance
      • Separation of concerns with custom hooks
      • State management strategies for different scales
      • Performance optimization techniques

      When structuring your project, consider organizing by feature rather than file type. This approach keeps related code together and makes it easier to understand the codebase as it grows.

      Modern React applications benefit from tools like TypeScript for type safety, ESLint for code quality, and testing libraries like React Testing Library for confidence in your code.
    `,
    date: "December 28, 2024",
    readTime: "8 min read",
    category: "React",
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800",
  },
  {
    id: 3,
    title: "StyleX: The Future of CSS-in-JS",
    excerpt:
      "Discover how StyleX revolutionizes styling in React with atomic CSS generation and compile-time optimization.",
    content: `
      StyleX represents a paradigm shift in how we think about styling React applications. Developed by Meta, it combines the developer experience of CSS-in-JS with the performance benefits of atomic CSS.

      What makes StyleX special:
      • Zero runtime overhead - styles are extracted at build time
      • Atomic CSS generation for minimal CSS bundle size
      • Type-safe styles with full TypeScript support
      • Deterministic style resolution

      Unlike traditional CSS-in-JS solutions, StyleX doesn't require a runtime. This means faster page loads and better performance metrics.

      The atomic approach means styles are deduplicated automatically, resulting in significantly smaller CSS bundles as your application grows.
    `,
    date: "December 20, 2024",
    readTime: "6 min read",
    category: "CSS",
    image: "https://images.unsplash.com/photo-1507721999472-8ed4421c4af2?w=800",
  },
  {
    id: 4,
    title: "Mastering TypeScript Generics",
    excerpt:
      "A comprehensive guide to understanding and effectively using TypeScript generics in your projects.",
    content: `
      TypeScript generics are one of the most powerful features for building reusable, type-safe code. They allow you to write functions and classes that work with multiple types while maintaining full type safety.

      Understanding generics:
      • Generic functions for flexible, reusable utilities
      • Generic constraints to limit acceptable types
      • Generic interfaces for defining contracts
      • Conditional types for advanced type manipulation

      Generics shine when building utility functions, data structures, and API wrappers. They help catch errors at compile time rather than runtime.

      Start simple and gradually adopt more complex patterns as you become comfortable with the basics.
    `,
    date: "December 15, 2024",
    readTime: "10 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=800",
  },
  {
    id: 5,
    title: "Performance Optimization Techniques for React",
    excerpt:
      "Explore advanced techniques to optimize your React application for lightning-fast performance.",
    content: `
      Performance is crucial for user experience. Slow applications lead to frustrated users and lost conversions. React provides several tools and patterns to help optimize your applications.

      Key optimization strategies:
      • React.memo for preventing unnecessary re-renders
      • useMemo and useCallback for expensive computations
      • Code splitting with React.lazy and Suspense
      • Virtual lists for rendering large datasets

      Always measure before optimizing. Use React DevTools Profiler and browser performance tools to identify actual bottlenecks.

      Remember: premature optimization is the root of all evil. Focus on writing clean, maintainable code first, then optimize based on real performance data.
    `,
    date: "December 10, 2024",
    readTime: "7 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
  },
  {
    id: 6,
    title: "Building Real-time Applications with WebSockets",
    excerpt:
      "Learn how to implement real-time features in your web applications using WebSocket technology.",
    content: `
      WebSockets enable real-time, bidirectional communication between clients and servers. They're perfect for applications like chat, live updates, collaborative editing, and gaming.

      WebSocket fundamentals:
      • Persistent connections reduce latency
      • Full-duplex communication for instant updates
      • Event-driven architecture for scalability
      • Reconnection strategies for reliability

      When implementing WebSockets, consider using libraries like Socket.io or ws for Node.js. These handle many complexities like reconnection and fallbacks.

      Always implement proper error handling and reconnection logic. Network connections can be unreliable, especially on mobile devices.
    `,
    date: "December 5, 2024",
    readTime: "9 min read",
    category: "WebSocket",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
  },
  {
    id: 7,
    title: "Modern State Management with Zustand",
    excerpt:
      "Discover why Zustand is becoming the go-to state management library for React developers.",
    content: `
      Zustand offers a refreshingly simple approach to state management in React. With minimal boilerplate and a hooks-first API, it's become a favorite among developers seeking simplicity without sacrificing power.

      Why choose Zustand:
      • Minimal boilerplate compared to Redux
      • No providers needed - just import and use
      • Built-in support for persistence
      • Devtools integration for debugging

      Zustand stores are just JavaScript objects, making them easy to understand and debug. You can create multiple stores for different concerns or use a single store for your entire application.

      The library also supports middleware for logging, persistence, and other cross-cutting concerns.
    `,
    date: "December 1, 2024",
    readTime: "5 min read",
    category: "State Management",
    image: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800",
  },
];

export const CATEGORIES = [
  "All",
  "React",
  "TypeScript",
  "CSS",
  "Performance",
  "WebSocket",
  "State Management",
  "Full Stack",
];

export const WORK_EXPERIENCE = [
  {
    company: "Universal Orthodontic Lab, Inc.",
    role: "Senior Software Engineer",
    period: "April 8, 2025 - Present",
    description:
      "Focusing on frontend development to build and maintain high-quality user interfaces.",
    tech: ["React", "JavaScript", "Frontend"],
  },
  {
    company: "Laro",
    role: "Senior Software Engineer",
    period: "March 2, 2025 - Present",
    description:
      "Specializing in mobile application development while providing cross-functional support for the web platform.",
    tech: ["React Native", "Mobile", "Web"],
  },
  {
    company: "Cauld & Clark Developers",
    role: "Senior Software Engineer",
    period: "March 1, 2025 - Present",
    description:
      "Developing and maintaining robust, client-facing web applications using a diverse range of modern technologies.",
    tech: ["Web Development", "Frontend", "Backend"],
  },
  {
    company: "Toggle Innovations, Inc",
    role: "Junior Software Engineer",
    period: "July 2023 - May 2024",
    description:
      "Developed and maintained multiple client-facing web applications.",
    tech: ["Android Studio", "PHP", "Framework7", "MySQL"],
  },
  {
    company: "Corporate Technologies, inc. (Filinvest)",
    role: "Associate App Support",
    period: "April 2022 - October 2022",
    description:
      "Assisted in the resolution of application issues and provided technical support to end-users. Collaborated with the development team to identify and resolve underlying issues.",
    tech: ["Technical Support", "Troubleshooting"],
  },
];

export const TECH_STACKS = [
  { name: "Apollo GraphQL", category: "API" },
  { name: "C", category: "Language" },
  { name: "CSS", category: "Language" },
  { name: "Docker", category: "DevOps" },
  { name: "Expo", category: "Mobile" },
  { name: "Git", category: "Version Control" },
  { name: "GitHub", category: "Version Control" },
  { name: "Go", category: "Backend" },
  { name: "GraphQL", category: "API" },
  { name: "HTML", category: "Language" },
  { name: "Husky", category: "DevOps" },
  { name: "JavaScript", category: "Language" },
  { name: "Jest", category: "Testing" },
  { name: "Material-UI", category: "Styling" },
  { name: "MongoDB", category: "Database" },
  { name: "MQTT", category: "IoT" },
  { name: "MySQL", category: "Database" },
  { name: "Next.js", category: "Frontend" },
  { name: "Node.js", category: "Backend" },
  { name: "PostCSS", category: "Styling" },
  { name: "PostgreSQL", category: "Database" },
  { name: "Python", category: "Backend" },
  { name: "React", category: "Frontend" },
  { name: "React Native", category: "Mobile" },
  { name: "Redis", category: "Database" },
  { name: "Redux", category: "State Management" },
  { name: "Relay", category: "State Management" },
  { name: "Rust", category: "Systems" },
  { name: "Socket.io", category: "Real-time" },
  { name: "SQL", category: "Database" },
  { name: "StyleX", category: "Styling" },
  { name: "Tailwind CSS", category: "Styling" },
  { name: "TanStack Query", category: "State Management" },
  { name: "TypeScript", category: "Language" },
  { name: "Vitest", category: "Testing" },
  { name: "Webpack", category: "Build Tools" },
  { name: "WebSocket", category: "Real-time" },
  { name: "Zustand", category: "State Management" },
];

export const PROJECTS = [
  {
    id: 1,
    title: "Simple App",
    description:
      "A production-ready social platform featuring a news feed with infinite scroll, real-time messaging, and high-performance media stories. Built using Go, React, and MQTT for zero-latency interactions.",
    tech: ["Go", "React", "GraphQL", "MQTT", "PostgreSQL", "Relay"],
    image: "images/projects/simpleapp.png",
    github: null,
    demo: null,
    featured: true,
  },
  {
    id: 2,
    title: "Nytron",
    description:
      "A custom programming language built from scratch using C. Designed to explore language design principles, lexical analysis, parsing, and code generation. A passion project demonstrating low-level systems programming expertise.",
    tech: ["C", "Compiler Design", "Systems Programming", "Language Design"],
    image: "images/projects/nytron.png",
    github: null,
    demo: null,
    featured: true,
  },
  {
    id: 3,
    title: "Cauld & Clark Developers",
    description:
      "The official company website for Cauld & Clark Developers, showcasing our services, portfolio, and expertise. Built with modern web technologies featuring a sleek design and optimal user experience.",
    tech: ["React", "JavaScript", "CSS", "Web Development"],
    image: "images/projects/cauldnclark.png",
    github: null,
    demo: "https://cauldnclark.com",
    featured: true,
  },
  {
    id: 4,
    title: "Laro",
    description:
      "A sports platform that enables users to book courts, organize matches, and find games near them. Features game discovery, court listings, leaderboards, and community engagement tools for sports enthusiasts in the Philippines.",
    tech: ["React Native", "React", "Web Development", "Mobile"],
    image: "images/projects/laro.png",
    github: null,
    demo: "https://laro.com.ph",
    featured: true,
  },
  {
    id: 5,
    title: "Cerlucent",
    description:
      "An AI-driven dental restorations and lab services platform. Features advanced manufacturing with robotics, automation, AI, and computer vision to deliver precision dental crowns and bridges for dental professionals.",
    tech: ["React", "AI", "Web Development", "Automation"],
    image: "images/projects/cerlucent.png",
    github: null,
    demo: "https://cerlucent.com",
    featured: true,
  },
  {
    id: 6,
    title: "The Journal",
    description:
      "A social networking site developed during college in 2018. Built as a platform for users to connect with their community. An early project that sparked a passion for web development.",
    tech: ["PHP", "MySQL", "HTML", "CSS", "JavaScript"],
    image: "images/projects/thejournal.jpg",
    github: null,
    demo: null,
    featured: false,
  },
  {
    id: 7,
    title: "Snow Cabal",
    description:
      "A Cabal Online private server project. As owner and developer, I manage the game server on Debian Linux with Docker and SQL database. The game client is written using C, XML, and Assembly. Features a React-powered website for the community.",
    tech: [
      "React",
      "Linux",
      "Docker",
      "C",
      "SQL",
      "XML",
      "Assembly",
      "Game Development",
    ],
    image: "images/projects/snowcabal.jpg",
    github: null,
    demo: null,
    featured: false,
  },
];
